import { useEffect, useState } from 'react'
import { useParams } from 'react-router-dom'

type State =
  | { status: 'loading' }
  | { status: 'valid'; label: string; keyMode: string; token: string }
  | { status: 'invalid'; heading: string; body: string }

const ERROR_MESSAGES: Record<string, { heading: string; body: string }> = {
  INVITE_NOT_FOUND: {
    heading: 'Invitation not found',
    body: 'This invite link is not valid. It may have been mistyped. Ask for a new link.',
  },
  INVITE_REVOKED: {
    heading: 'Invitation revoked',
    body: 'This invite has been revoked. Ask for a new invitation.',
  },
  INVITE_USED: {
    heading: 'Invitation already used',
    body: 'This invite has already been accepted. If you have an account, sign in from the home page.',
  },
  INVITE_EXPIRED: {
    heading: 'Invitation expired',
    body: 'This invite link has expired. Ask for a renewed link.',
  },
}

const BACKEND = import.meta.env.VITE_API_URL ?? ''

export default function InvitePage() {
  const { token } = useParams<{ token: string }>()
  const [state, setState] = useState<State>({ status: 'loading' })

  useEffect(() => {
    if (!token) {
      setState({
        status: 'invalid',
        heading: 'Invalid link',
        body: 'No invitation token found in this link.',
      })
      return
    }

    fetch(`${BACKEND}/auth/invite/${encodeURIComponent(token)}`)
      .then(async (res) => {
        const data = await res.json()
        if (!res.ok) {
          const code: string = data?.error?.code ?? ''
          const msg = ERROR_MESSAGES[code] ?? {
            heading: 'Invitation invalid',
            body: 'This invite link cannot be used. Ask the admin for a new link.',
          }
          setState({ status: 'invalid', ...msg })
        } else {
          setState({ status: 'valid', label: data.label, keyMode: data.key_mode, token })
        }
      })
      .catch(() => {
        setState({
          status: 'invalid',
          heading: 'Could not verify invitation',
          body: 'A network error occurred. Please try again.',
        })
      })
  }, [token])

  return (
    <div className="min-h-screen bg-stone-50 flex flex-col">
      <main className="flex-1 flex items-center justify-center px-4">
        <div className="max-w-sm w-full text-center space-y-6">
          {state.status === 'loading' && (
            <p className="text-stone-500 text-sm">Verifying your invitation…</p>
          )}

          {state.status === 'valid' && (
            <>
              <div className="space-y-2">
                <h1 className="text-xl font-semibold text-stone-900">
                  You've been invited, {state.label}
                </h1>
                <p className="text-stone-500 text-sm">
                  Sign in with Google to accept your invitation and set up your Proof of Work
                  account. You can use any Google account you'd like.
                </p>
              </div>

              <a
                href={`${BACKEND}/auth/google?invite_token=${encodeURIComponent(state.token)}`}
                className="inline-flex items-center gap-2 px-5 py-2.5 bg-stone-800 text-white text-sm font-medium rounded-lg hover:bg-stone-700 transition-colors"
              >
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
                </svg>
                Continue with Google
              </a>
            </>
          )}

          {state.status === 'invalid' && (
            <>
              <div className="space-y-2">
                <h1 className="text-xl font-semibold text-stone-900">{state.heading}</h1>
                <p className="text-stone-500 text-sm leading-relaxed">{state.body}</p>
              </div>
              <a
                href="/"
                className="inline-block px-4 py-2 bg-stone-800 text-white text-sm font-medium rounded-lg hover:bg-stone-700 transition-colors"
              >
                Back to sign in
              </a>
            </>
          )}
        </div>
      </main>

      <footer className="py-4 text-center text-xs text-stone-400">
        <a href="/privacy" className="hover:text-stone-600 transition-colors">
          Privacy
        </a>
      </footer>
    </div>
  )
}
